/**
 * Referral capture + persistence.
 *
 * Referral attribution depends on the referrer's code (`?ref=<id>`) surviving
 * the FULL journey: link click -> (maybe an OAuth redirect out to Google and
 * back, maybe an in-app-browser -> real-browser hand-off) -> account creation.
 *
 * The backend attributes correctly whenever `referredBy` reaches it, so the only
 * way a referral is silently lost is if the code never makes it that far on the
 * client. Two concrete client-side gaps this module closes:
 *
 *   1. Timing: components used to read `?ref` only on mount. On entry URLs that
 *      immediately redirect (e.g. `/` -> `/login`, which drops the query string
 *      BEFORE React effects run), the code was gone before anything captured it.
 *      Capturing at module load — before React/Router mounts — removes that race
 *      and works for ANY entry route (`/`, `/login`, `/dashboard`, ...).
 *
 *   2. Durability: the referral must outlive redirects and be readable from
 *      every login path (browser OAuth, native Android sign-in). localStorage
 *      under a single stable key is the shared source of truth.
 *
 * The storage key is intentionally the same `hardayReferrer` used elsewhere, so
 * this is a strictly additive, earlier + more robust capture of the SAME value —
 * no behavioural change to how the code is later read or sent.
 */

export const REFERRAL_STORAGE_KEY = 'hardayReferrer';
export const REFERRAL_QUERY_PARAM = 'ref';

const safeGetSearch = () => {
  try {
    return (typeof window !== 'undefined' && window.location && window.location.search) || '';
  } catch {
    return '';
  }
};

/**
 * Read `?ref=` from a search string (defaults to the current URL) and, when
 * present and non-empty, persist it to localStorage. Returns the referral code
 * that is now stored (freshly captured, or the pre-existing stored value), or
 * null if none is available.
 *
 * A ref present in the URL always wins over (and overwrites) a stale stored one
 * so a user who follows a NEW referral link is attributed to the new referrer.
 */
export const captureReferralFromUrl = (search = safeGetSearch()) => {
  let code = null;
  try {
    code = new URLSearchParams(search || '').get(REFERRAL_QUERY_PARAM);
  } catch {
    code = null;
  }

  const cleaned = (code || '').trim();
  if (cleaned) {
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, cleaned);
    } catch {
      /* localStorage may be unavailable (private mode / webview) — the in-URL
         value is still carried through the OAuth redirect as a query param, so
         attribution is not solely dependent on this write succeeding. */
    }
    return cleaned;
  }

  return getStoredReferral();
};

/** The referral code currently persisted for this browser, or null. */
export const getStoredReferral = () => {
  try {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
    return stored && stored.trim() ? stored.trim() : null;
  } catch {
    return null;
  }
};

/** Clear the stored referral (call after it has been attributed at login). */
export const clearStoredReferral = () => {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
